import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isDicomEditActive } from '@/hooks/use-dicom-edit-job';
import { formatNumber } from '@/lib/format';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
/** How long a finished update stays mentioned on the row. */
export const DICOM_EDIT_DONE_VISIBLE_MS = 24 * 60 * 60 * 1000;
/**
 * One line under the expanded row's actions: a DICOM update in progress, just
 * finished (for a day), or failed. Renders nothing otherwise.
 */
export function DicomEditStatusLine({ job, now, }) {
    const { t } = useTranslation();
    const [mountedAt] = useState(() => Date.now());
    const at = now ?? mountedAt;
    if (!job)
        return null;
    if (isDicomEditActive(job)) {
        return (_jsxs("p", { className: "text-muted-foreground flex items-center gap-2 px-2 pb-2 text-xs", "data-testid": "dicom-edit-status", children: [_jsx(Loader2, { className: "h-3.5 w-3.5 animate-spin", "aria-hidden": true }), t('worklist.dicomEdit.progress', {
                    processed: formatNumber(job.processedImages),
                    total: formatNumber(job.totalImages),
                })] }));
    }
    if (job.state === 'failed') {
        return (_jsxs("p", { className: "text-destructive flex items-center gap-2 px-2 pb-2 text-xs", "data-testid": "dicom-edit-status", children: [_jsx(AlertTriangle, { className: "h-3.5 w-3.5", "aria-hidden": true }), job.error
                    ? t('worklist.dicomEdit.failedWithError', { error: job.error })
                    : t('worklist.dicomEdit.failed')] }));
    }
    const completedAt = job.completedAt ? Date.parse(job.completedAt) : NaN;
    if (Number.isNaN(completedAt) || at - completedAt > DICOM_EDIT_DONE_VISIBLE_MS)
        return null;
    return (_jsxs("p", { className: "flex items-center gap-2 px-2 pb-2 text-xs text-green-400", "data-testid": "dicom-edit-status", children: [_jsx(CheckCircle2, { className: "h-3.5 w-3.5", "aria-hidden": true }), job.skippedImages > 0
                ? t('worklist.dicomEdit.doneSkipped', {
                    count: job.skippedImages,
                    number: formatNumber(job.skippedImages),
                })
                : t('worklist.dicomEdit.done')] }));
}
